import { PropsWithChildren, ReactNode } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { Checkbox, CheckboxProps } from '@spark-web/checkbox';
import { Text } from '@spark-web/text';
import type { FormFieldProps } from '../Form';

interface WithLabel {
  label: string;
  children?: never;
}

interface WithChildren {
  label?: never;
  children: ReactNode;
}

export type CheckboxFieldProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  FormFieldProps<TFieldValues>,
  'label'
> &
  Omit<CheckboxProps, 'children' | 'dangerouslySetInnerHTML'> &
  (WithLabel | WithChildren) & {
    /** When provided, stores this string when checked and '' when unchecked instead of a boolean. */
    checkedValue?: string;
  };

function CheckboxField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  shouldUnregister,
  children,
  rules,
  checkedValue,
  ...checkboxProps
}: PropsWithChildren<CheckboxFieldProps<TFieldValues>>) {
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules: {
      ...rules,
      onChange: checkboxProps.onChange,
      onBlur: checkboxProps.onBlur,
    },
    shouldUnregister,
  });

  const handleChange =
    checkedValue !== undefined
      ? (e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked ? checkedValue : '')
      : undefined;

  return (
    <Checkbox
      {...checkboxProps}
      {...field}
      {...(handleChange && { onChange: handleChange })}
      tone={error ? 'critical' : 'neutral'}
      message={error?.message}
      checked={checkedValue !== undefined ? !!field.value : field.value}
      contentEditable="inherit"
    >
      {label ? <Text>{label}</Text> : children}
    </Checkbox>
  );
}

export default CheckboxField;
